import * as React from 'react';

/** Bumps on window resize (rAF-coalesced) so geometry-derived observers rebuild. */
function useViewportGeometryVersion(enabled: boolean): number {
  const [version, setVersion] = React.useState(0);
  React.useEffect(() => {
    if (!enabled) return undefined;
    let frame = 0;
    const schedule = (): void => {
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setVersion((current) => current + 1);
      });
    };
    window.addEventListener('resize', schedule);
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
    };
  }, [enabled]);
  return version;
}

/**
 * Viewport line under the nav where it docks (its sticky `top` plus its own
 * height), not where it happens to sit before the page scrolls.
 */
function dockedNavBottom(navigation: HTMLElement): number {
  const stickyTop = Number.parseFloat(getComputedStyle(navigation).top);
  const top = Number.isFinite(stickyTop) ? stickyTop : navigation.getBoundingClientRect().top;
  return Math.round(top + navigation.offsetHeight);
}

/**
 * Scroll-spy for the report section nav: the active entry is the section
 * whose box crosses the line just below the docked nav. An
 * IntersectionObserver with a viewport root works through nested scroll
 * containers (the shell outlet, a workspace body), so no scroll listener on
 * any particular scroller.
 *
 * Returns the spied section, or the route's `routeActiveId` until the reader
 * has scrolled a section under the nav (or when spying is disabled).
 */
export function useReportNavScrollSpy({
  itemIds,
  enabled,
  navigationRef,
  routeActiveId,
}: {
  itemIds: string;
  enabled: boolean;
  navigationRef: React.RefObject<HTMLElement | null>;
  routeActiveId: string | undefined;
}): string | undefined {
  const [activeId, setActiveId] = React.useState<string | undefined>(undefined);
  const geometryVersion = useViewportGeometryVersion(enabled);

  React.useEffect(() => {
    // A new item set or a re-enabled spy starts from the route's selection,
    // never from an id spied in an earlier generation.
    setActiveId(undefined);
    const navigation = navigationRef.current;
    if (!enabled || !navigation || typeof IntersectionObserver === 'undefined') return undefined;
    const ids = itemIds.split('|').filter(Boolean);
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((section): section is HTMLElement => section !== null);
    if (sections.length === 0) return undefined;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const lineTop = Math.min(viewportHeight - 1, Math.max(1, dockedNavBottom(navigation) + 1));
    const bottomTrim = Math.max(1, viewportHeight - lineTop - 1);
    const rootMargin = `-${lineTop}px 0% -${bottomTrim}px 0%`;
    const crossing = new Set<string>();
    let frame = 0;
    const publish = (): void => {
      frame = 0;
      const first = ids.find((id) => crossing.has(id));
      if (first) setActiveId(first);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) crossing.add(entry.target.id);
          else crossing.delete(entry.target.id);
        }
        if (frame === 0) frame = window.requestAnimationFrame(publish);
      },
      { rootMargin, threshold: 0 },
    );
    sections.forEach((section) => observer.observe(section));
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [enabled, itemIds, navigationRef, geometryVersion]);

  return (enabled ? activeId : undefined) ?? routeActiveId;
}
